import React, { useCallback, useContext, useEffect, useState } from "react";
import Keycloak from "keycloak-js";
import GlobalState from "./GlobalState";
import PropTypes from "prop-types";
import { getLocaleText } from "utils/general";

/**
 * Keycloak instance
 */
const keycloak = new Keycloak({
	url: process.env.REACT_APP_KEYCLOAK_URL,
	realm: process.env.REACT_APP_KEYCLOAK_REALM,
	clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID,
});

/**
 * Authentication provider component.
 * 
 * The component initializes authentication and refreshes token when it expires.
 */
const KeycloakAuthProvider = props => {
	const [state, setState] = useContext(GlobalState)
	const [auth, setAuth] = useState(undefined);

	  // Listen for logout event
	  useEffect(() => {
		const handleLocalStorageLogout = event => {
		  if (event.key === "logout") {
			if (state.logout) {
			  state.logout()
			}
		  }
		}

		globalThis.addEventListener("storage", handleLocalStorageLogout)
		return () => globalThis.removeEventListener("storage", handleLocalStorageLogout)
	  }, [state.logout])

	/**
	 * Updates authentication data.
	 * Method is called when user logs in or when token is refreshed.
	 * keycloak.profile
	 */
	const updateAuthData = useCallback(() => {
		if (!(keycloak.tokenParsed && keycloak.token)) return;

		localStorage.setItem("api_token", keycloak.token);
		setState(state => ({ ...state, logout: () => keycloak.logout({redirectUri: `${globalThis.location.origin}` + '/' }) }))

		if (keycloak.tokenParsed?.realm_access?.roles) {
			setState(state => ({ ...state, realm_roles: keycloak.tokenParsed.realm_access.roles }))
		}
		else {
			setState(state => ({ ...state, realm_roles: [] }))
		}

		setAuth({
			token: keycloak.tokenParsed,
			tokenRaw: keycloak.token,
			logout: () => keycloak.logout({ redirectUri: `${globalThis.location.origin}` }),
		});
	}, [setAuth]);

	/**
	 * Clears authentication data.
	 * Method is called when user logs out.
	 */
	const clearAuthData = useCallback(() => {
		//console.log("Keycloak clear auth data")
		setAuth(undefined);
		localStorage.setItem("api_token", "");
	}, [setAuth]);

	/**
   * Initializes authentication.
   * Method is called when component mounts.
   */
	const initAuth = useCallback(async () => {
		const keycloakLoginOptions = {
			redirectUri: `${globalThis.location.origin}/main`
		}

		try {
			keycloak.onTokenExpired = () => {
				keycloak.updateToken(5);
			}

			keycloak.onAuthRefreshError = () => {
				console.log("Keycloak onAuthRefreshError")
				keycloak.login(keycloakLoginOptions);
			}
			keycloak.onAuthRefreshSuccess = () => {
				updateAuthData();
			}

			keycloak.onAuthError = (error) => console.error(error);
			keycloak.onAuthSuccess = async () => {
				try {
					await keycloak.loadUserProfile();
				} catch (error) {
					console.error("Could not load user profile", error);
				}

				updateAuthData();
			};

			// Triggers only with status iframe enabled 
			keycloak.onAuthLogout = () => {
				console.log("Keycloak onAuthLogout")
				clearAuthData();
				keycloak.login(keycloakLoginOptions);
			};

			let locale = getLocaleText() ?? "fi"
			await keycloak.init({
				onLoad: "login-required",
				checkLoginIframe: false,
				scope: "openid profile email",
				locale: locale
			});
		} catch (error) {
			console.error(error);
		}
	}, [clearAuthData, updateAuthData]);

	/**
	 * Initializes authentication when component mounts.
	 */
	useEffect(() => {
		if (keycloak.authenticated === undefined) initAuth();
	}, []);

	if (!auth) {
		return (
            <div id="preloader">
              <div id="status">
                <div className="spinner-chase">
                  <div className="chase-dot" />
                  <div className="chase-dot" />
                  <div className="chase-dot" />
                  <div className="chase-dot" />
                  <div className="chase-dot" />
                  <div className="chase-dot" />
                </div>
              </div>
            </div>
          )
	}
	return props.children;
}

KeycloakAuthProvider.propTypes = {
    children: PropTypes.any,
  }

export default KeycloakAuthProvider;